<template>
  <CDialog
    v-bind="{ show }"
    @close="closeModal"
    :title="$t('application_for_transfer')"
  >
    <div class="p-5">
      <FRadioGroup
        wrapper-class="flex items-center gap-2 mb-4"
        v-model="transferValue"
        :items="transferList"
        label-key="label"
        value-key="value"
      />
      <CollapseTransition name="fade">
        <div class="flex gap-4" v-if="transferValue === 'withdrawal'">
          <div class="p-4 border border-gray-800 rounded-2xl">
            <h4 class="text-dark mb-2 text-base font-semibold leading-normal">
              {{ $t("to_withdrawal") }}
            </h4>
            <p class="text-xs font-normal leading-130 text-[#535E69]">
              {{ $t("referral_transfer_title") }}
            </p>
          </div>
        </div>
      </CollapseTransition>
      <form @submit.prevent="onUpdate">
        <FGroup :label="$t('sum_of_fill')" class="my-4">
          <FInput
            v-maska="moneyMask"
            v-model="form.values.amount"
            :error="form.$v.value.amount?.$error"
            :maxlength="18"
            placeholder="100 000"
          />
          <div class="w-full flex-y-center justify-between">
            <div>
              <span class="text-gray text-xs">
                {{ $t("min_funds", { money: 50 }) }} USD
              </span>
            </div>
            <div v-if="transferValue === 'withdrawal'">
              <span class="text-gray text-xs capitalize">{{
                $t("with_commission", {
                  commission: formatMoneyDecimal(amountWithCommission ?? 0),
                })
              }}</span>
            </div>
          </div>
        </FGroup>
        <CButton
          :text="$t('save')"
          class="w-full mt-6"
          size="sm"
          :disabled="isLoading"
          :loading="isLoading"
          type="submit"
        />
      </form>
    </div>
  </CDialog>
</template>

<script setup lang="ts">
import CDialog from "@/components/Common/Dialog/CDialog.vue";
import FGroup from "@/components/Form/FGroup.vue";
import FInput from "@/components/Form/Input/FInput.vue";
import { formatMoneyDecimal, moneyMask } from "@/utils";
import CButton from "@/components/Common/CButton.vue";
import { useForm } from "@/composables/useForm";
import { required, minValue } from "@vuelidate/validators";
import ApiService from "@/services/ApiService";
import { useCustomToast } from "@/composables/useCustomToast";
import { useI18n } from "vue-i18n";
import { computed, ref, onMounted, watch } from "vue";
import { useAuthStore } from "@/modules/Auth/stores";
import FRadioGroup from "@/components/Form/Radio/FRadioGroup.vue";
import CollapseTransition from "@ivanv/vue-collapse-transition/src/CollapseTransition.vue";
import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
import { trackCustomEvent } from "@/utils/fbq";

const { showToast } = useCustomToast();
const { t } = useI18n();
const { pushToDataLayer } = usePushToDataLayer();

interface Props {
  show: boolean;
}
const props = defineProps<Props>();
const isLoading = ref(false);

const emit = defineEmits<{
  (e: "updateTransactions"): void;
  (e: "close"): void;
}>();
const transferValue = ref("balance");

const commission = ref(0.02);
const transferList = ref([
  {
    label: t("to_balance"),
    value: "balance",
  },
  {
    label: t("to_withdrawal"),
    value: "withdrawal",
  },
]);
const authStore = useAuthStore();

const form = useForm(
  {
    amount: "",
  },
  {
    amount: { required, minValue: minValue(50) },
  },
);

const amountWithCommission = computed(() => {
  if (!form.values.amount) return 0;
  const amount = Number(form.values.amount.replace(/\s/g, ""));
  return amount - amount * commission.value;
});
const onUpdate = () => {
  form.$v.value.$touch();
  if (!form.$v.value.$invalid) {
    isLoading.value = true;
    form.values.amount = form.values.amount.replace(/\s/g, "");
    if (transferValue.value === "balance") {
      transferToBalance();
    } else {
      transferToWithdrawal();
    }
  } else {
    // Track validation errors
    pushToDataLayer("transfer_validation_error", {
      transfer_type: transferValue.value,
      has_amount_error: form.$v.value.amount.$error,
      event_category: "validation",
      event_label: "transfer_validation_error",
      button_text: "Save",
    });
  }
};

const transferToBalance = () => {
  // Track transfer to balance attempt
  pushToDataLayer("transfer_to_balance_attempt", {
    amount: form.values.amount,
    event_category: "wallet",
    event_label: "transfer_referral_to_balance",
    button_text: "Save",
  });
  trackCustomEvent("UserTransferReferralBalanceAttempt", {
    event_category: "wallet",
    event_label: "transfer_referral_to_balance",
    button_text: "Save",
  });

  ApiService.post("/payment/transfer-referral-balance/", {
    amount: form.values.amount,
  })
    .then(() => {
      showToast(t("successfully_send"), "success");
      authStore.fetchUserData();
      emit("updateTransactions");
      closeModal();

      // Track successful transfer to balance
      pushToDataLayer("transfer_to_balance_success", {
        amount: form.values.amount,
        event_category: "wallet",
        event_label: "transfer_referral_to_balance_success",
        button_text: "Save",
      });
      trackCustomEvent("UserTransferReferralBalanceSuccess", {
        event_category: "wallet",
        event_label: "transfer_referral_to_balance_success",
        button_text: "Save",
      });
    })
    .catch((err) => {
      let errorType = "unknown";
      if (
        err?.response?.data?.error === "Minimum withdrawal amount is 50 USDT"
      ) {
        showToast(t("not_enough_min_referral_money"), "error");
        errorType = "minimum_amount_not_met";
      }
      if (err?.response?.data?.error === "Insufficient referral balance") {
        showToast(t("not_enough_referral_money"), "error");
        errorType = "insufficient_balance";
      }

      // Track failed transfer to balance
      pushToDataLayer("transfer_to_balance_error", {
        amount: form.values.amount,
        error_type: errorType,
        event_category: "wallet",
        event_label: "transfer_referral_to_balance_error",
        button_text: "Save",
      });

      return new Error(err);
    })
    .finally(() => {
      isLoading.value = false;
    });
};
const transferToWithdrawal = () => {
  // Track transfer to withdrawal attempt
  pushToDataLayer("transfer_to_withdrawal_attempt", {
    amount: form.values.amount,
    commission: commission.value,
    amount_with_commission: amountWithCommission.value,
    event_category: "wallet",
    event_label: "transfer_referral_to_withdrawal",
    button_text: "Save",
  });
  trackCustomEvent("UserTransferReferralWithdrawalAttempt", {
    event_category: "wallet",
    event_label: "transfer_referral_to_withdrawal",
    button_text: "Save",
  });

  ApiService.post("/payment/request-withdrawal/", {
    amount: form.values.amount,
  })
    .then(() => {
      showToast(t("successfully_send"), "success");
      authStore.fetchUserData();
      emit("updateTransactions");
      closeModal();

      // Track successful transfer to withdrawal
      pushToDataLayer("transfer_to_withdrawal_success", {
        amount: form.values.amount,
        commission: commission.value,
        amount_with_commission: amountWithCommission.value,
        event_category: "wallet",
        event_label: "transfer_referral_to_withdrawal_success",
        button_text: "Save",
      });
      trackCustomEvent("UserTransferReferralWithdrawalSuccess", {
        event_category: "wallet",
        event_label: "transfer_referral_to_withdrawal_success",
        button_text: "Save",
      });
    })
    .catch((err) => {
      let errorType = "unknown";
      if (err?.response?.data?.error === "Insufficient referral balance") {
        showToast(t("not_enough_referral_money"), "error");
        errorType = "insufficient_balance";
      }
      if (
        err?.response?.data?.error === "Minimum withdrawal amount is 50 USDT"
      ) {
        showToast(t("not_enough_min_referral_money"), "error");
        errorType = "minimum_amount_not_met";
      }

      // Track failed transfer to withdrawal
      pushToDataLayer("transfer_to_withdrawal_error", {
        amount: form.values.amount,
        commission: commission.value,
        amount_with_commission: amountWithCommission.value,
        error_type: errorType,
        event_category: "wallet",
        event_label: "transfer_referral_to_withdrawal_error",
        button_text: "Save",
      });

      return new Error(err);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const closeModal = () => {
  // Track when the dialog is closed
  pushToDataLayer("transfer_dialog_closed", {
    transfer_type: transferValue.value,
    amount_entered: !!form.values.amount,
    event_category: "wallet",
    event_label: "transfer_dialog_closed",
    button_text: "Close",
  });

  form.values.amount = "";
  form.$v.value.$reset();
  emit("close");
};

// Track transfer type changes
watch(transferValue, (newValue) => {
  pushToDataLayer("transfer_type_changed", {
    transfer_type: newValue,
    event_category: "wallet",
    event_label: "transfer_type_selected",
    button_text: "None",
  });
});

// Track dialog opening
onMounted(() => {
  if (props.show) {
    pushToDataLayer("transfer_dialog_opened", {
      event_category: "wallet",
      event_label: "transfer_dialog_view",
      button_text: "None",
    });
  }
});

watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      pushToDataLayer("transfer_dialog_opened", {
        event_category: "wallet",
        event_label: "transfer_dialog_view",
        button_text: "None",
      });
    }
  },
);
</script>

<style scoped>
.qr-code {
  border-radius: 16px;
  border: 2px solid rgba(22, 204, 83, 0.5);
  background: linear-gradient(180deg, #16cc53 -230.8%, #080a15 110.94%);
}

canvas {
  display: inline-block;
  width: 82px;
  height: 82px;
}
</style>
